<template>
    <div v-if="colleagues.length" class="collaboration-participants" role="group" aria-label="Сейчас работают вместе с вами">
        <component :is="canFollow(person) ? 'button' : 'span'" v-for="person in visiblePeople" :key="person.user_id"
              v-b-tooltip.hover.focus.bottom="participantTitle(person)"
              class="collaboration-participants__person" :style="{'--participant-color': person.color}"
              :class="{'collaboration-participants__person--follow': canFollow(person), 'collaboration-participants__person--idle': person.active === false}"
              :type="canFollow(person) ? 'button' : null" tabindex="0" :aria-label="participantTitle(person)"
              @click="follow(person)">
            <employee-avatar :avatar-url="person.avatar_url" :name="person.display_name" :show-title="false" :size="28" />
        </component>
        <b-dropdown v-if="hiddenPeople.length" size="sm" variant="link" no-caret right
                    :text="`+${hiddenPeople.length}`" :aria-label="hiddenNames">
            <b-dropdown-item-button v-for="person in hiddenPeople" :key="person.user_id"
                                    :disabled="!canFollow(person)" @click="follow(person)">
                <employee-avatar :class="{'collaboration-participants__person--idle': person.active === false}"
                                 :avatar-url="person.avatar_url" :name="person.display_name" :show-title="false" :size="24" />
                {{ person.display_name }}
            </b-dropdown-item-button>
        </b-dropdown>
    </div>
</template>

<script>
import EmployeeAvatar from './EmployeeAvatar.vue';
import {socket} from '@/plugins/socket';
import {createCollaborationClient, otherParticipants} from '@/utils/collaborationPresence';

export default {
    name: 'CollaborationParticipants',
    components: {EmployeeAvatar},
    props: {
        scope: {type: String, required: true},
        resourceId: {type: [String, Number], default: null},
        ready: {type: Boolean, default: false},
        followableUserIds: {type: Array, default: () => []}
    },
    data() { return {participants: [], selfUserId: null}; },
    computed: {
        subscriptionKey() { return `${this.scope}:${this.resourceId || ''}:${this.ready}`; },
        colleagues() { return otherParticipants(this.participants, this.selfUserId); },
        visiblePeople() { return this.colleagues.slice(0, 5); },
        hiddenPeople() { return this.colleagues.slice(5); },
        hiddenNames() { return this.hiddenPeople.map(person => person.display_name).join('; '); }
    },
    watch: {subscriptionKey() { this.updatePresence(); }},
    mounted() {
        this._lastPresenceActivity = Date.now();
        this._presenceClient = createCollaborationClient({
            socket, clientId: `screen-${this._uid}-${Date.now()}`,
            timers: window,
            isActive: () => document.visibilityState !== 'hidden' && Date.now() - this._lastPresenceActivity < 120000,
            onChange: value => { this.participants = value.participants; this.selfUserId = value.self_user_id; }
        });
        document.addEventListener('visibilitychange', this.onVisibilityChanged);
        ['pointerdown', 'pointermove', 'keydown', 'scroll'].forEach(event => document.addEventListener(event, this.onActivity, {passive: true, capture: true}));
        window.addEventListener('pagehide', this.onPageHide);
        window.addEventListener('pageshow', this.updatePresence);
        this.updatePresence();
    },
    beforeDestroy() {
        document.removeEventListener('visibilitychange', this.onVisibilityChanged);
        ['pointerdown', 'pointermove', 'keydown', 'scroll'].forEach(event => document.removeEventListener(event, this.onActivity, true));
        window.removeEventListener('pagehide', this.onPageHide);
        window.removeEventListener('pageshow', this.updatePresence);
        if (this._presenceClient) this._presenceClient.destroy();
        this._presenceClient = null;
    },
    methods: {
        canFollow(person) { return this.followableUserIds.includes(Number(person.user_id)); },
        participantTitle(person) {
            const name = `${person.display_name}${person.active === false ? ' — нет активности' : ''}`;
            return this.canFollow(person) ? `${name} — перейти к ячейкам` : name;
        },
        follow(person) { if (this.canFollow(person)) this.$emit('select', person); },
        updatePresence() {
            if (this._presenceClient) this._presenceClient.update({
                scope: this.scope, resource_id: this.resourceId,
                ready: this.ready && (this.scope !== 'timesheet' || Number(this.resourceId) > 0)
            });
        },
        onVisibilityChanged() {
            if (document.visibilityState !== 'hidden') this._lastPresenceActivity = Date.now();
            if (this._presenceClient) this._presenceClient.visibilityChanged();
        },
        onActivity() {
            this._lastPresenceActivity = Date.now();
            if (this._presenceClient) this._presenceClient.activityChanged();
        },
        onPageHide() {
            if (this._presenceClient) this._presenceClient.update({scope: this.scope, ready: false});
        }
    }
};
</script>

<style scoped>
.collaboration-participants { display: inline-flex; align-items: center; flex: 0 0 auto; padding: 2px; gap: 4px; }
.collaboration-participants__person { display: inline-flex; padding: 0; background: transparent; border: 2px solid var(--participant-color); border-radius: 50%; cursor: help; }
.collaboration-participants__person--follow { cursor: pointer; }
.collaboration-participants__person--idle { opacity: .4; filter: grayscale(.7); }
.collaboration-participants__person:focus, .collaboration-participants__more:focus { outline: 2px solid #305f94; outline-offset: 2px; }
.collaboration-participants__more { display: inline-flex; align-items: center; justify-content: center; height: 32px; min-width: 32px; padding: 0 5px; border-radius: 50%; background: #edf2f7; color: #405268; font-size: 12px; font-weight: 600; cursor: help; }
@media (max-width: 575.98px) { .collaboration-participants { gap: 2px; } }
</style>
